import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { FieldConfig } from "../../../interfaces/field.interface";
import { TranslateModule } from "@ngx-translate/core";
import { IonicModule, ToastController } from "@ionic/angular";
import { InstituicaoProvider } from "src/app/services/instituicao/instituicao";
import { CommonModule } from "@angular/common";
import intlTelInput from "intl-tel-input";
import { NgxMaskDirective } from "ngx-mask";

@Component({
  selector: "app-input-mask",
  template: `
    <ion-item [formGroup]="group">
      <div class="alinhamento-div">
        <label position="stacked" class="custom-label">
          {{ (field.label ?? '') | translate }}
          <span
            *ngIf="field.validations && field.validations[0] && field.validations[0].name == 'required'"
            class="danger"
            >*</span
          >
        </label>

        <input
          *ngIf="field?.mask?.type === 'custom'"
          [type]="field.inputType"
          [formControlName]="field.name"
          [mask]="mask.mask"
          [placeholder]="(field.label ?? '') | translate"
        />

        <input
          *ngIf="field?.mask?.type === 'phone'"
          #phoneInput
          [type]="field.inputType"
          [formControlName]="field.name"
          placeholder="{{'Digite o número de telefone' | translate }}"
          class="intl-tel-input"
          id="phone"
          [mask]="maskTel"
        />
      </div>
    </ion-item>

    <div class="validation-errors">
      <ng-container *ngFor="let validation of field.validations">
        <div class="error-message"
        *ngIf="group.get(field.name)?.hasError(validation.name) && group.get(field.name)?.touched"

        >
          {{ "Campo Obrigatório" | translate }}
        </div>
      </ng-container>
    </div>
  `,
  styles: [
    `
      ion-item {
        --inner-border-width: 0;
        padding: 10px 0;
        margin: 0 20px;
      }

      ion-input {
        font-size: 14px;
        color: #787486;
        min-height: 25px !important;
        padding: 0 !important;
      }

      input {
        font-size: 14px;
        color: #787486;
        min-height: 25px !important;
        border: none;
        width: 100%;
      }

      input:focus {
        outline: none !important;
        box-shadow: none !important;
        border-color: transparent !important;
      }

      .alinhamento-div {
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      .custom-label {
        font-size: 15px;
        font-weight: 500;
      }

      .error-message {
        padding-bottom: 10px !important;
        font-size: 12px !important;
      }

      .validation-errors {
        margin-left: 35px;
      }
    `,
  ],
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule, FormsModule, TranslateModule, NgxMaskDirective],
})
export class InputMaskComponent implements OnInit {
  @ViewChild("phoneInput", { read: ElementRef }) phoneInput: ElementRef;
  field!: FieldConfig;
  group!: FormGroup;
  mask: any = {};
  lastIso2!: string;
  lastIsoMaster!: string;
  maskTel: string;

  constructor(
    public instituicaoProvider: InstituicaoProvider,
    public toastCtrl: ToastController,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    if (this.field.mask) {
      if (this.field.mask.type === "phone") {
        setTimeout(() => {
          this.inputMask();
          this.cdr.detectChanges();
        }, 0);
      } else if (this.field.mask.type === "custom" && this.field.mask.custom) {
        this.mask = {
          mask: this.field.mask.custom,
          len: this.field.mask.custom.length,
        };
      }
    }
  }

  ngAfterViewInit() { }

  inputMask() {
    const inputElement = this.phoneInput.nativeElement;

    const fieldProp =
      this.field.ddiTelCelular ||
      this.field.ddiTelResidencial ||
      this.field.ddiTelContato ||
      this.field.ddiTelComercial;

    if (fieldProp) {
      this.lastIso2 = (this.instituicaoProvider.objDddi() as { [key: string]: string })[fieldProp];
      this.lastIsoMaster = this.lastIso2;
      this.applyPhoneMask();
    }

    const iti = intlTelInput(inputElement, {
      countryOrder: ["Us", "Gb", "Br", "Es", "Sa", "Ca"],
      separateDialCode: true,
      initialCountry: this.lastIso2,
      formatOnDisplay: true,
      formatAsYouType: true,
      countrySearch: false,
    });

    inputElement.addEventListener("countrychange", () => {
      const selectedCountryData = iti.getSelectedCountryData();

      const fieldMappings: { [key: string]: string } = {
        telCelular: "ddiTelCelular",
        telResidencial: "ddiTelResidencial",
        telContato: "ddiTelContato",
        telComercial: "ddiTelComercial",
      };

      const fieldProp = fieldMappings[this.field.name];
      if (fieldProp) {
        this.field[fieldProp] = "+" + selectedCountryData.dialCode;
        this.group.get(fieldProp)?.setValue("+" + selectedCountryData.dialCode);
      }

      if (selectedCountryData) {
        this.lastIso2 = selectedCountryData.iso2 || "";
        this.lastIsoMaster = selectedCountryData.iso2 || "";
        this.applyPhoneMask();
      } else {
        this.applyPhoneMask();
      }
    });

    inputElement.addEventListener("input", (event: any) => {
      if (event.inputType === "deleteContentBackward") {
        this.lastIso2 = this.lastIsoMaster;
      }
      this.applyPhoneMask();
    });
  }

  async applyPhoneMask() {
    const objectMask: { [key: string]: string } = {
      ac: "0000",
      ad: "000-000",
      ae: "00-000-0000",
      af: "00-000-0000",
      ag: "(000)000-0000",
      ai: "(000)000-0000",
      al: "(000)000-000",
      am: "00-000-000",
      an: "0000-0000",
      ao: "(000)000-000",
      aq: "000-000",
      ar: "(000)000-0000",
      as: "(000)000-0000",
      at: "(000)000-0000",
      au: "0-0000-0000",
      aw: "000-0000",
      az: "00-000-00-00",
      ba: "00-00000",
      bb: "(000)000-0000",
      bd: "00-000-000",
      be: "(000)000-000",
      bf: "00-00-0000",
      bg: "(000)000-000",
      bh: "0000-0000",
      bi: "00-00-0000",
      bj: "00-00-0000",
      bm: "(000)000-0000",
      bn: "000-0000",
      bo: "0-000-0000",
      br: "(00)00000-0000",
      bs: "(000)000-0000",
      bt: "00-000-000",
      bw: "00-000-000",
      by: "(00)000-00-00",
      bz: "000-0000",
      ca: "(000)000-0000",
      cd: "(000)000-000",
      cf: "00-00-0000",
      cg: "00-000-0000",
      ch: "00-000-0000",
      ci: "00-000-000",
      ck: "00-000",
      cl: "0-0000-0000",
      cm: "0000-0000",
      cn: "(000)0000-0000",
      co: "(000)000-0000",
      cr: "0000-0000",
      cu: "0-000-0000",
      cv: "(000)00-00",
      cw: "000-0000",
      cy: "00-000-000",
      cz: "(000)000-000",
      de: "(0000)000-0000",
      dj: "00-00-00-00-00",
      dk: "00-00-00-00-00",
      dm: "(000)000-0000",
      do: "(000)000-0000",
      dz: "00-000-0000",
      ec: "00-000-0000",
      ee: "0000-0000",
      eg: "(000)000-0000",
      er: "0-000-000",
      es: "(000)000-000",
      et: "00-000-0000",
      fi: "(000)000-00-00",
      fj: "00-00000",
      fk: "00000",
      fm: "000-0000",
      fo: "000-000",
      fr: "(000)000-000",
      ga: "0-00-00-00-00",
      gd: "(000)000-0000",
      ge: "(000)000-000",
      gf: "00000-0000",
      gh: "(000)000-000",
      gi: "000-00000",
      gl: "00-00-00-00",
      gm: "(000)00-00",
      gn: "00-000-000",
      gq: "00-000-0000",
      gr: "(000)000-0000",
      gt: "0-000-0000",
      gu: "(000)000-0000",
      gw: "0-000000",
      gy: "000-0000",
      hk: "0000-0000",
      hn: "0000-0000",
      hr: "00-000-000",
      ht: "00-00-0000",
      hu: "(000)000-000",
      id: "(000)000-00-000",
      ie: "(000)000-000",
      il: "00-000-0000",
      in: "(0000)000-000",
      io: "000-0000",
      iq: "(000)000-0000",
      ir: "(000)000-0000",
      is: "000-0000",
      it: "(000)0000-000",
      jm: "(000)000-0000",
      jo: "0-0000-0000",
      jp: "(000)000-000",
      ke: "000-000-0000",
      kg: "(000)000-000",
      kh: "00-000-000",
      ki: "00-000",
      km: "00-00000",
      kn: "(000)000-0000",
      kp: "000-0000-000",
      kr: "00-000-0000",
      kw: "0000-0000",
      ky: "(000)000-0000",
      kz: "(000)000-00-00",
      la: "(0000)000-000",
      lb: "00-000-000",
      lc: "(000)000-0000",
      li: "(000)000-0000",
      lk: "00-000-0000",
      lr: "00-000-000",
      ls: "0-000-0000",
      lt: "(000)00-000",
      lu: "(000)000-000",
      lv: "00-000-000",
      ly: "00-000-000",
      ma: "00-0000-000",
      mc: "(000)000-000",
      md: "0000-0000",
      me: "00-000-000",
      mg: "00-00-00000",
      mh: "000-0000",
      mk: "00-000-000",
      ml: "00-00-0000",
      mm: "00-000-000",
      mn: "00-00-0000",
      mo: "0000-0000",
      mp: "(000)000-0000",
      mq: "(000)00-00-00",
      mr: "00-00-0000",
      ms: "(000)000-0000",
      mt: "0000-0000",
      mu: "000-0000",
      mv: "000-0000",
      mw: "0-0000-0000",
      mx: "(000)000-0000",
      my: "(000)000-000",
      mz: "00-000-000",
      na: "00-000-0000",
      nc: "00-0000",
      ne: "00-00-0000",
      nf: "000-000",
      ng: "(000)000-0000",
      ni: "0000-0000",
      nl: "00-000-0000",
      no: "(000)00-000",
      np: "00-000-000",
      nr: "000-0000",
      nu: "0000",
      nz: "(000)000-000",
      om: "00-000-000",
      pa: "000-0000",
      pe: "(000)000-000",
      pf: "00-00-00",
      pg: "(000)00-000",
      ph: "(000)000-0000",
      pk: "(000)000-0000",
      pl: "(000)000-000",
      ps: "00-000-0000",
      pt: "00-000-0000",
      pw: "000-0000",
      py: "(000)000-000",
      qa: "0000-0000",
      re: "00000-0000",
      ro: "00-000-0000",
      rs: "00-000-0000",
      ru: "(000)000-00-00",
      rw: "(000)000-000",
      sa: "(0000) 000 000",
      sb: "000-0000",
      sc: "0-000-000",
      sd: "00-000-0000",
      se: "00-000-0000",
      sg: "0000-0000",
      sh: "0000",
      si: "00-000-000",
      sk: "(000)000-000",
      sl: "00-000000",
      sm: "0000-000000",
      sn: "00-000-0000",
      so: "00-000-000",
      sr: "000-0000",
      ss: "00-000-0000",
      st: "00-00000",
      sv: "00-00-0000",
      sx: "(000)000-0000",
      sy: "00-0000-000",
      sz: "00-00-0000",
      tc: "(000)000-0000",
      td: "00-00-00-00",
      tg: "00-000-000",
      th: "00-000-0000",
      tj: "00-000-0000",
      tk: "0000",
      tl: "000-0000",
      tm: "0-000-0000",
      tn: "00-000-000",
      to: "00000",
      tr: "(000)000-0000",
      tt: "(000)000-0000",
      tv: "000000",
      tw: "0-0000-0000",
      tz: "00-000-0000",
      ua: "(00)000-00-00",
      ug: "(000)000-000",
      gb: "00-0000-000000",
      us: "(000)000-0000",
      uy: "0-000-00-00",
      uz: "00-000-0000",
      va: "0-000-00000",
      vc: "(000)000-0000",
      ve: "(000)000-0000",
      vg: "(000)000-0000",
      vi: "(000)000-0000",
      vn: "(000)0000-000",
      vu: "00-00000",
      wf: "00-0000",
      ws: "00-0000",
      ye: "000-000-000",
      za: "00-000-0000",
      zm: "00-000-0000",
      zw: "0-000000",
    };

    const numberMask = objectMask[this.lastIso2].replace(/\D/g, "");
    const maskObj = {
      pais: this.lastIso2,
      mask: objectMask[this.lastIso2],
      maxDigits: numberMask.length,
    };

    this.maskTel = maskObj.mask;
    this.cdr.detectChanges();
  }
}
